import getAddinCollection from './getAddinCollection';
import { ContextualMenuItemType } from 'office-ui-fabric-react/lib/ContextualMenu';
import { AddinCommandSurfaceItem, ExtensibilityModeEnum } from 'owa-addins-types';
import { isFolderOfTreeType, SHARED_FOLDERS_TREE_TYPE } from 'owa-folders';
import Item from 'owa-service/lib/contract/Item';
import { DIVIDER_DISPLAY_NAME, SurfaceActionItem } from 'owa-surface-actions';
import {
    convertToIContextualMenuItem,
    getReadHostItemIndex,
    getComposeHostItemIndex,
} from 'owa-addins-core';

function addItemToSurfaceCollection(
    surfaceItems: SurfaceActionItem[],
    item: SurfaceActionItem,
    isEndAligned: boolean
) {
    // Mark the item and all subMenu items as pinned for telemetry.
    item.isPinned = true;
    if (item.subMenuProps) {
        item.subMenuProps.items.forEach(subItem => {
            subItem.isPinned = true;
        });
    }

    if (isEndAligned) {
        surfaceItems.unshift(item);
    } else {
        surfaceItems.push(item);
    }
}

function tryAddDivider(overflowItems: SurfaceActionItem[], dividerKey: number) {
    const itemLength = overflowItems.length;
    if (itemLength > 0 && overflowItems[itemLength - 1].displayName != DIVIDER_DISPLAY_NAME) {
        overflowItems.push({
            key: `addinDivider_${dividerKey}`,
            displayName: DIVIDER_DISPLAY_NAME,
            itemType: ContextualMenuItemType.Divider,
            onClick: null,
        } as SurfaceActionItem);
    }
}

export default function populateCommandBarWithAddins(
    extMode: ExtensibilityModeEnum,
    item: Item,
    hostId: string,
    surfaceAddinKeys: string[],
    surfaceItems: SurfaceActionItem[],
    overflowItems: SurfaceActionItem[],
    isEndAligned: boolean,
    dividerKey: number,
    className?: any,
    alwaysAddToOverflow?: boolean,
    isSharedItem?: boolean,
    isDisbaled?: boolean
) {
    let installedAddins: AddinCommandSurfaceItem[];
    isSharedItem = isSharedItem || getIsSharedItem(item);
    const addinCollection = getAddinCollection(extMode, isSharedItem);
    installedAddins = addinCollection ? addinCollection.sectionProps.items : [];
    const hostItemIndex =
        extMode == ExtensibilityModeEnum.MessageRead
            ? getReadHostItemIndex(hostId)
            : getComposeHostItemIndex(hostId);
    let shouldAddFirstDivider = true;
    installedAddins.forEach(addin => {
        convertToIContextualMenuItem(hostItemIndex, addin);
        const surfaceActionItem = addin as SurfaceActionItem;
        surfaceActionItem.iconOnly = true;
        if (className) {
            surfaceActionItem.className = className;
        }

        //Disable add-in surface button
        surfaceActionItem.disabled = isDisbaled ? true : false;

        // Check if the user has this addin in their surface addins.
        if (surfaceAddinKeys.indexOf(addin.key) >= 0) {
            // If they do, create a 'clone' initially pointing to the same object.
            let surfaceActionItemClone = surfaceActionItem;
            if (alwaysAddToOverflow) {
                // If we'll be adding this addin to the overflow as well, create a duplicate.
                // This wil allow us to modify the tool-tip on the surface without affecting the label in the menu.
                surfaceActionItemClone = { ...surfaceActionItem };
            }

            // The surfaceActionItem title is actually the description for the add-in.
            // The ariaLabel is equivalent to name + " - " + title
            // For the most part, we'd like to use the ariaLabel for the pinned surface actions,
            // but if the name and title are the same, we should just use the name.
            if (
                surfaceActionItemClone.name != undefined &&
                surfaceActionItemClone.title != undefined &&
                surfaceActionItemClone.name.trim() != surfaceActionItemClone.title.trim()
            ) {
                surfaceActionItemClone.name = surfaceActionItemClone.ariaLabel;
            }
            surfaceActionItemClone.title = '';
            addItemToSurfaceCollection(surfaceItems, surfaceActionItemClone, isEndAligned);
        }

        // If the user doesn't have this addin in their surface addins, or we should always add to the overflow,
        // push it to the overflow, but first add a divider to separate addins from actions if needed.
        if (surfaceAddinKeys.indexOf(addin.key) == -1 || alwaysAddToOverflow) {
            if (shouldAddFirstDivider) {
                tryAddDivider(overflowItems, dividerKey++);
                shouldAddFirstDivider = false;
            }

            // Mark the item and all subMenu items as rendered in overflow for telemetry.
            surfaceActionItem.renderedInOverflow = true;
            if (surfaceActionItem.subMenuProps) {
                surfaceActionItem.subMenuProps.items.forEach(subItem => {
                    subItem.renderedInOverflow = true;
                });
            }
            // Only push dividers if the overflow menu is non-empty
            if (
                surfaceActionItem.itemType !== ContextualMenuItemType.Divider ||
                (overflowItems.length !== 0 &&
                    overflowItems[overflowItems.length - 1].itemType !==
                        ContextualMenuItemType.Divider)
            ) {
                overflowItems.push(surfaceActionItem);
            }
        }
    });
}

function getIsSharedItem(item: Item) {
    if (!item || !item.ParentFolderId) {
        return false;
    }

    const folderId = item.ParentFolderId.Id;
    return isFolderOfTreeType(folderId, SHARED_FOLDERS_TREE_TYPE);
}
